import { useState, useMemo, useCallback, useEffect } from "react";
import Button from "@mui/material/Button";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";

import Spinner from "@components/Spinner";
import {
  fetchMiscellaneous,
  createMiscellaneous,
  deleteMiscellaneous,
  fetchBillablePlants,
} from "@service/arInvoiceApiService";
import { PlantOption } from "@service/PlantService";
import FacilityService from "@service/FacilityService";
import { IFacilityDTO } from "@interfaces/IFacilityDTO";

interface MiscellaneousRow {
  id: number;
  Description: string | null;
  Notes: string | null;
  Amount: number | null;
  "Plant ID": number | null;
  Plant: string | null;
  "Facility ID": number | null;
  Facility: string | null;
  "Invoice Code": string | null;
  "Invoice ID": number | null;
  "Invoice #": string | null;
  "Billed Week": string | null;
}

function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// The earliest selectable pay period: the Sunday that begins the previous week.
function getPreviousWeekSunday(): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const currentWeekSunday = new Date(today);
  currentWeekSunday.setDate(today.getDate() - today.getDay());
  const previousWeekSunday = new Date(currentWeekSunday);
  previousWeekSunday.setDate(currentWeekSunday.getDate() - 7);
  return formatDate(previousWeekSunday);
}

// A valid pay period is a Sunday on/after the previous week's Sunday.
function isValidPayPeriod(periodStart: string): boolean {
  if (!periodStart) return false;
  const selected = new Date(periodStart + "T00:00:00");
  if (selected.getDay() !== 0) return false;
  return selected.getTime() >= new Date(getPreviousWeekSunday() + "T00:00:00").getTime();
}

function getEndDateStr(startStr: string): string {
  const start = new Date(startStr + "T00:00:00");
  start.setDate(start.getDate() + 6);
  const year = start.getFullYear();
  const month = String(start.getMonth() + 1).padStart(2, "0");
  const day = String(start.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const currencyFormatter = (params: { value: any }) => {
  const val = parseFloat(params.value);
  if (params.value == null || isNaN(val)) return "";
  const sign = val < 0 ? "-" : "";
  return `${sign}$${Math.abs(val).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const safeSizeColumnsToFit = (params: { api?: any }) => {
  const api = params.api;
  if (!api || api.isDestroyed?.()) return;
  try {
    api.sizeColumnsToFit();
  } catch {
    /* grid not ready / no longer mounted — ignore */
  }
};

interface MiscellaneousTabProps {
  periodStart: string;
  // Bumped by the parent after invoices are generated/canceled, so the grid
  // repopulates (linked misc become voided, new active copies appear).
  refreshSignal?: number;
  // Called after a miscellaneous is created/deleted, so the parent can refresh
  // the Invoices tab (un-invoiced totals changed).
  onAfterChange?: () => void;
}

export function MiscellaneousTab({
  periodStart,
  refreshSignal,
  onAfterChange,
}: MiscellaneousTabProps) {
  const [rows, setRows] = useState<MiscellaneousRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add-miscellaneous dialog
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [payPeriod, setPayPeriod] = useState(periodStart);
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [amount, setAmount] = useState("");
  const [plantId, setPlantId] = useState<number | "">("");
  const [facilityId, setFacilityId] = useState<number | "">("");

  // Plant / facility options for the form
  const [plants, setPlants] = useState<PlantOption[]>([]);
  const [facilities, setFacilities] = useState<IFacilityDTO[]>([]);
  const [facilitiesLoading, setFacilitiesLoading] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<MiscellaneousRow | null>(
    null
  );
  const [deleting, setDeleting] = useState(false);

  const fetchData = useCallback(async (startStr: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchMiscellaneous({
        start_date: startStr,
        end_date: getEndDateStr(startStr),
      });
      setRows(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to fetch miscellaneous");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(periodStart);
  }, [periodStart, refreshSignal, fetchData]);

  // Load billable plant options once (for the add form).
  useEffect(() => {
    fetchBillablePlants()
      .then((res) => setPlants(Array.isArray(res.data) ? res.data : []))
      .catch(() => setPlants([]));
  }, []);

  // Load facilities whenever the selected plant changes in the form.
  useEffect(() => {
    if (plantId === "") {
      setFacilities([]);
      return;
    }
    setFacilitiesLoading(true);
    FacilityService.getFacilitiesByPlant(plantId)
      .then((data) => setFacilities(data))
      .catch(() => setFacilities([]))
      .finally(() => setFacilitiesLoading(false));
  }, [plantId]);

  const openAdd = () => {
    // Default the pay period to the one currently selected on the page.
    setPayPeriod(periodStart);
    setDescription("");
    setNotes("");
    setAmount("");
    setPlantId("");
    setFacilityId("");
    setFacilities([]);
    setFormError(null);
    setAddOpen(true);
  };

  const handlePlantChange = (value: number | "") => {
    setPlantId(value);
    setFacilityId("");
  };

  const handleSave = async () => {
    setFormError(null);
    if (!description.trim()) {
      setFormError("Description is required.");
      return;
    }
    const amountNum = parseFloat(amount);
    if (amount === "" || isNaN(amountNum)) {
      setFormError("Amount must be a number.");
      return;
    }
    if (plantId === "") {
      setFormError("Plant is required.");
      return;
    }
    if (!payPeriod) {
      setFormError("Pay period is required.");
      return;
    }
    if (!isValidPayPeriod(payPeriod)) {
      setFormError(
        "Pay period must be a Sunday on or after the previous week's Sunday."
      );
      return;
    }
    setSaving(true);
    try {
      await createMiscellaneous({
        start_date: payPeriod,
        end_date: getEndDateStr(payPeriod),
        description: description.trim(),
        notes: notes.trim() || null,
        amount: amountNum,
        plant_id: plantId,
        facility_id: facilityId === "" ? null : facilityId,
      });
      setAddOpen(false);
      await fetchData(periodStart);
      onAfterChange?.();
    } catch (err: any) {
      setFormError(
        err?.response?.data?.error || "Failed to create miscellaneous."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteMiscellaneous(deleteTarget.id);
      setDeleteTarget(null);
      await fetchData(periodStart);
      onAfterChange?.();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Failed to delete miscellaneous.");
    } finally {
      setDeleting(false);
    }
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      { field: "Description", headerName: "Description", flex: 1, minWidth: 200 },
      { field: "Notes", headerName: "Notes", flex: 1, minWidth: 180 },
      {
        field: "Amount",
        headerName: "Amount",
        width: 140,
        valueFormatter: currencyFormatter,
      },
      { field: "Plant", headerName: "Plant", width: 200 },
      { field: "Facility", headerName: "Facility", width: 160 },
      { field: "Invoice Code", headerName: "Inv. Code", width: 120 },
      {
        field: "Invoice #",
        headerName: "Invoice #",
        width: 150,
        valueFormatter: (params) => params.value || "Not invoiced",
      },
      {
        headerName: "",
        field: "actions",
        width: 80,
        sortable: false,
        filter: false,
        pinned: "right",
        cellRenderer: (params: any) => {
          const linked = params.data?.["Invoice ID"] != null;
          return (
            <Tooltip
              title={
                linked
                  ? "Cannot delete — already linked to an invoice"
                  : "Delete miscellaneous"
              }
            >
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={linked}
                  onClick={() => setDeleteTarget(params.data)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          );
        },
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({ sortable: true, resizable: true, filter: true, minWidth: 90 }),
    []
  );

  return (
    <div className="tw-w-full">
      {error && (
        <div className="tw-text-center tw-py-4 tw-text-red-600 tw-flex tw-justify-center">
          <div>{error}</div>
        </div>
      )}

      {isLoading && (
        <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-min-w-[580px] tw-min-h-[55vh]">
          <Spinner className="tw-flex tw-justify-center tw-ml-2 tw-items-center tw-w-full" />
          Loading miscellaneous...
        </div>
      )}

      {!isLoading && (
        <div className="tw-bg-white tw-p-4">
          <div className="tw-flex tw-justify-end tw-mb-2">
            <Button
              variant="contained"
              sx={{ bgcolor: "#2642c9", textTransform: "none" }}
              startIcon={<AddIcon />}
              onClick={openAdd}
            >
              Add Miscellaneous
            </Button>
          </div>
          <div
            className="ag-theme-balham tw-w-full"
            style={{ height: Math.min(rows.length * 28 + 56, 500) }}
          >
            <AgGridReact
              rowData={rows}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              animateRows={true}
              pagination={false}
              overlayNoRowsTemplate="<span></span>"
              onGridReady={safeSizeColumnsToFit}
              onGridSizeChanged={safeSizeColumnsToFit}
            />
          </div>
        </div>
      )}

      {/* Add miscellaneous dialog */}
      <Dialog
        open={addOpen}
        onClose={() => !saving && setAddOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Miscellaneous</DialogTitle>
        <DialogContent dividers>
          {formError && (
            <div className="tw-mb-3 tw-p-2 tw-rounded tw-bg-red-100 tw-text-red-700 tw-text-sm">
              {formError}
            </div>
          )}
          <div className="tw-flex tw-flex-col tw-gap-4 tw-mt-1">
            <TextField
              label="Pay Period (week beginning Sunday)"
              type="date"
              value={payPeriod}
              onChange={(e) => setPayPeriod(e.target.value)}
              fullWidth
              size="small"
              required
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: getPreviousWeekSunday() }}
              helperText="Must be a Sunday on or after the previous week's Sunday."
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              size="small"
              required
            />
            <TextField
              label="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={2}
            />
            <TextField
              label="Amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              fullWidth
              size="small"
              required
              inputProps={{ step: "0.01" }}
              helperText="Use a negative amount for credits/deductions."
            />
            <TextField
              label="Plant"
              select
              value={plantId}
              onChange={(e) =>
                handlePlantChange(
                  e.target.value === "" ? "" : Number(e.target.value)
                )
              }
              fullWidth
              size="small"
              required
            >
              {plants.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.value}
                </MenuItem>
              ))}
            </TextField>
            {plantId !== "" && facilities.length > 0 && (
              <TextField
                label="Facility"
                select
                value={facilityId}
                onChange={(e) =>
                  setFacilityId(
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                fullWidth
                size="small"
                helperText={facilitiesLoading ? "Loading facilities..." : ""}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {facilities.map((f) => (
                  <MenuItem key={f.id} value={f.id}>
                    {[f.type, f.invoice_code].filter(Boolean).join(" - ")}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </div>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setAddOpen(false)}
            disabled={saving}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ bgcolor: "#2642c9", textTransform: "none" }}
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <Spinner className="tw-w-4 tw-h-4" /> : <AddIcon />}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={deleteTarget !== null}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>
          <p>
            Delete miscellaneous{" "}
            <strong>{deleteTarget?.Description ?? ""}</strong> (
            {currencyFormatter({ value: deleteTarget?.Amount })})?
          </p>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
            sx={{ textTransform: "none" }}
          >
            Keep
          </Button>
          <Button
            variant="contained"
            color="error"
            sx={{ textTransform: "none" }}
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Confirm Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default MiscellaneousTab;
